import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import {
  Star,
  Users,
  Globe,
  CheckCircle2,
  Play,
  ShieldCheck,
  Smartphone,
  Infinity,
  Trophy,
  ArrowLeft,
  ChevronRight,
  ShoppingCart,
  Zap
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import toast, { Toaster } from 'react-hot-toast';
import { buyCourse, getMyPurchases } from "../services/purchase";


export default function CourseDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [images, setImages] = useState([]);
  const [activeImage, setActiveImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPurchased, setIsPurchased] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [courseRes, imagesRes, purchaseRes] = await Promise.all([
          api.get(`/courses`),
          api.get(`/courses/${courseId}/images`),
          getMyPurchases()
        ]);

        const foundCourse = courseRes.data.find(c => c.id === parseInt(courseId));
        setCourse(foundCourse);

        const imgs = imagesRes.data || [];
        setImages(imgs);
        if (imgs.length > 0) {
          const preview = imgs.find(img => img.is_preview === 1) || imgs[0];
          setActiveImage(preview.image_url);
        } else if (foundCourse) {
          setActiveImage(`https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&auto=format&fit=crop`);
        }

        const purchased = purchaseRes.data.some(p => p.id === parseInt(courseId));
        setIsPurchased(purchased);

      } catch (err) {
        toast.error("Failed to load course details");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [courseId]);
  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleBuy = async () => {
    if (isPurchased) {
      navigate(`/course/${courseId}`);
      return;
    }

    try {
      const isScriptLoaded = await loadRazorpay();
      if (!isScriptLoaded) {
        toast.error("Payment system failed to load. Please check network.");
        return;
      }

      // Send exact price to create order and fetch config key
      const [res, configRes] = await Promise.all([
        api.post("/payments/create-order", { amount: Math.round(course.price) }),
        api.get("/payments/config").catch(() => ({ data: {} }))
      ]);
      const { id, amount } = res.data;
      const rzpKey = configRes.data.razorpay_key || "rzp_test_ShJUkFoOhGho5K";

      const options = {
        key: rzpKey,
        amount,
        currency: "INR",
        name: "Antigravity LMS",
        description: `Purchase: ${course.title}`,
        order_id: id,
        handler: async function (response) {
          try {
            // Verifies transaction and persists purchase systematically
            await api.post("/payments/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              item_ids: [parseInt(courseId)]
            });

            toast.success("✅ Enrollment Successful!");
            setIsPurchased(true);
          } catch (err) {
            toast.error("Error logging transaction");
          }
        },
        prefill: {
          name: "Student",
          email: "student@example.com"
        },
        theme: { color: "#2563eb" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error(err);
      toast.error("Order generation failed");
    }
  };

  const handleAddToCart = () => {
    const cart = JSON.parse(localStorage.getItem("cart") || "[]");
    if (cart.some(item => item.id === course.id)) {
      toast.error("Course already in cart!");
      return;
    }
    cart.push({ id: course.id, title: course.title, price: course.price });
    localStorage.setItem("cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cartUpdated"));
    toast.success("Added to Cart! 🛒");
  };


  if (loading) return (
    <div className="flex h-screen items-center justify-center dark:bg-slate-950">
      <div className="w-10 h-10 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
    </div>
  );

  if (!course) return <div className="p-10 text-center">Course not found</div>;

  const discount = course.original_price > course.price
    ? Math.round(((course.original_price - course.price) / course.original_price) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Toaster position="top-center" />

      {/* HEADER SECTION */}
      <div className="bg-slate-900 text-white py-12 lg:py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4" />

        <div className="container mx-auto px-6 lg:px-20 relative z-10">
          <div className="max-w-3xl space-y-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-medium"
            >
              <ArrowLeft size={16} /> Back to Browse
            </button>

            <h1 className="text-3xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              {course.title}
            </h1>

            <p className="text-lg lg:text-xl text-slate-300 line-clamp-3">
              {course.description || "Master these skills with our comprehensive, expert-led course designed for all levels."}
            </p>

            <div className="flex flex-wrap items-center gap-6 pt-4 text-sm font-medium">
              <div className="flex items-center gap-1.5 text-amber-400">
                <Star size={18} fill="currentColor" />
                <span className="text-white font-bold">4.8</span>
                <span className="text-slate-400 font-normal underline">(12,453 ratings)</span>
              </div>
              <div className="flex items-center gap-1.5 text-slate-300">
                <Users size={18} />
                <span>154,234 students</span>
              </div>
              <div className="flex items-center gap-1.5 text-slate-300">
                <Globe size={18} />
                <span>English, Hindi</span>
              </div>
            </div>

            <div className="text-slate-400 text-sm">
              Created by <span className="text-blue-400 underline cursor-pointer">Expert Instructors</span> • Last updated 10/2023
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 lg:px-20 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

          {/* MAIN CONTENT */}
          <div className="lg:col-span-2 space-y-12">

            {/* GALLERY SECTION */}
            <section className="space-y-6">
              <h2 className="text-2xl font-bold dark:text-white">Course Preview</h2>
              <div className="card p-4 space-y-4">
                <div className="relative aspect-video rounded-2xl overflow-hidden group">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={activeImage}
                      src={activeImage}
                      alt="Course preview"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </AnimatePresence>
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors flex items-center justify-center cursor-zoom-in">
                    <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Play className="text-white fill-white" size={24} />
                    </div>
                  </div>
                </div>

                {images.length > 0 && (
                  <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin">
                    {images.map((img) => (
                      <button
                        key={img.id}
                        onClick={() => setActiveImage(img.image_url)}
                        className={`relative w-24 h-16 shrink-0 rounded-lg overflow-hidden border-2 transition-all ${activeImage === img.image_url ? "border-blue-500 scale-95 shadow-lg" : "border-transparent opacity-70 hover:opacity-100"
                          }`}
                      >
                        <img src={img.image_url} alt="Course preview" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* WHAT YOU'LL LEARN */}
            <section className="card p-8">
              <h2 className="text-2xl font-bold mb-6 dark:text-white">What you'll learn</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "Master the core concepts of the industry",
                  "Build real-world projects from scratch",
                  "Learn best practices used by professionals",
                  "Prepare for advanced certification exams",
                  "Solve complex architectural challenges",
                  "Optimize performance and security"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="text-green-500 shrink-0 mt-0.5" size={20} />
                    <span className="text-slate-700 dark:text-slate-300 text-[15px]">{item}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* CURRICULUM PREVIEW */}
            <section className="space-y-6">
              <h2 className="text-2xl font-bold dark:text-white">Course Content</h2>
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="card p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors cursor-pointer group">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-slate-400 group-hover:text-blue-500 transition-colors">
                        <Play size={18} />
                      </div>
                      <span className="font-bold text-slate-700 dark:text-slate-200">Section {i}: Introduction to the Fundamentals</span>
                    </div>
                    <span className="text-xs text-slate-400">4 lectures • 25min</span>
                  </div>
                ))}
                <button className="w-full py-3 text-sm font-bold text-blue-600 hover:text-blue-500 transition-colors">
                  Show all 12 sections
                </button>
              </div>
            </section>

          </div>

          {/* SIDEBAR CARD */}
          <div className="lg:relative">
            <div className="sticky top-24 space-y-6">
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="card overflow-hidden shadow-2xl border-none"
              >
                {/* Promo Media */}
                <div className="aspect-video relative overflow-hidden group cursor-pointer">
                  <img src={activeImage} alt="Course preview" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/30 group-hover:scale-110 transition-transform">
                      <Play className="text-white fill-white" size={24} />
                    </div>
                    <span className="text-white font-bold mt-4 text-sm">Preview this course</span>
                  </div>
                </div>

                <div className="p-8 space-y-6">
                  {/* Pricing */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-3">
                      <span className="text-4xl font-extrabold text-slate-900 dark:text-white">₹{course.price}</span>
                      {course.original_price > course.price && (
                        <>
                          <span className="text-lg text-slate-400 line-through">₹{course.original_price}</span>
                          <span className="text-lg font-bold text-green-500">{discount}% off</span>
                        </>
                      )}
                    </div>
                    <div className="text-red-500 text-sm font-bold flex items-center gap-1">
                      <Zap size={14} fill="currentColor" /> 5 hours left at this price!
                    </div>
                  </div>

                  <div className="space-y-3">
                    <button
                      onClick={handleAddToCart}
                      className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-extrabold rounded-xl shadow-lg shadow-blue-600/20 transition-all flex items-center justify-center gap-2 active:scale-[0.98]"
                    >
                      <ShoppingCart size={20} /> Add to Cart
                    </button>
                    <button
                      onClick={handleBuy}
                      className="w-full py-4 border-2 border-slate-900 dark:border-white text-slate-900 dark:text-white font-extrabold rounded-xl hover:bg-slate-900 hover:text-white dark:hover:bg-white dark:hover:text-slate-900 transition-all active:scale-[0.98]"
                    >
                      {isPurchased ? "Go to Course" : "Buy Now"}
                    </button>
                  </div>


                  <div className="text-center text-[11px] text-slate-500 font-medium">
                    30-Day Money-Back Guarantee
                  </div>

                  <div className="space-y-4 pt-4">
                    <div className="font-bold text-sm text-slate-900 dark:text-white">This course includes:</div>
                    <ul className="space-y-3">
                      {[
                        { icon: <Infinity size={16} />, text: "Full lifetime access" },
                        { icon: <Smartphone size={16} />, text: "Access on mobile and TV" },
                        { icon: <Trophy size={16} />, text: "Certificate of completion" },
                        { icon: <ShieldCheck size={16} />, text: "30-Day Money-Back Guarantee" }
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-3 text-slate-600 dark:text-slate-400 text-sm font-medium">
                          <span className="text-slate-400">{item.icon}</span>
                          {item.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>

              <div className="card p-6 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors cursor-pointer border-dashed">
                <div className="space-y-1">
                  <div className="font-bold text-sm dark:text-white">Apply Coupon</div>
                  <div className="text-xs text-slate-400 font-medium tracking-wide uppercase">LMS_NEW_2024</div>
                </div>
                <ChevronRight size={20} className="text-slate-400" />
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
